'use client';

import { useState } from 'react';
import { QR } from './qr-matrix';
import QrSvg from './QrSvg';

/**
 * §5 — the dashboard-mirror form card + live preview. The only interactivity
 * is the module-shape chips (they swap the preview's data-module path; the
 * preview box is fixed-width so swaps never move layout). Everything else is
 * a static picture of the real QR settings form — as shipped, no fake levers.
 * Finder eyes never restyle: QrSvg draws them from the shared square path.
 */

type Shape = 'square' | 'rounded' | 'dots';

const SHAPES: ReadonlyArray<{ key: Shape; label: string }> = [
  { key: 'square', label: 'SQUARE' },
  { key: 'rounded', label: 'ROUNDED' },
  { key: 'dots', label: 'DOTS' },
];

const PREVIEW_PATH: Record<Shape, string> = {
  square: QR.sq,
  rounded: QR.rd,
  dots: QR.dt,
};

export default function StudioConfigurator() {
  const [shape, setShape] = useState<Shape>('square');

  return (
    <div className="mt-11 flex flex-wrap items-stretch gap-6">
      <div className="flex min-w-[300px] flex-[1_1_400px] flex-col gap-[18px] rounded-ref border border-line-2 bg-surface px-[26px] py-6">
        <span className="font-jetbrains text-[10px] tracking-[0.14em] text-dim">
          DASHBOARD &middot; QR SETTINGS &mdash; AS SHIPPED
        </span>
        <div className="flex items-center gap-3.5 border-b border-line pb-4">
          <span
            aria-hidden="true"
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-line-2 bg-white font-onest text-[19px] font-extrabold"
          >
            A
          </span>
          <div className="flex flex-col gap-[3px]">
            <span className="text-[13px] font-semibold">Logo &mdash; acme-mark.png</span>
            <span className="text-[11.5px] text-dim">
              sits on a white chip; contrast is scanability
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-5 border-b border-line pb-4">
          <div className="flex items-center gap-[9px]">
            {/* Literal dashboard form values — the swatch shows the user's chosen fg color. */}
            <span className="h-[18px] w-[18px] rounded-[5px] border border-line-3 bg-[#101028]" />
            <span className="flex flex-col">
              <span className="font-jetbrains text-[10px] tracking-[0.08em] text-dim">
                FOREGROUND
              </span>
              <span className="font-jetbrains text-[11.5px]">#101028</span>
            </span>
          </div>
          <div className="flex items-center gap-[9px]">
            <span className="h-[18px] w-[18px] rounded-[5px] border border-line-3 bg-white" />
            <span className="flex flex-col">
              <span className="font-jetbrains text-[10px] tracking-[0.08em] text-dim">
                BACKGROUND
              </span>
              <span className="font-jetbrains text-[11.5px]">#FFFFFF</span>
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2.5 border-b border-line pb-4">
          <span id="qr-shape-label" className="font-jetbrains text-[10px] tracking-[0.08em] text-dim">
            MODULE SHAPE
          </span>
          <div role="group" aria-labelledby="qr-shape-label" className="flex flex-wrap gap-2.5">
            {SHAPES.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                aria-pressed={shape === key}
                onClick={() => setShape(key)}
                className={`cursor-pointer rounded-full border px-3.5 py-[7px] font-jetbrains text-[10.5px] tracking-[0.06em] transition-[border-color,background-color,color] duration-200 motion-reduce:transition-none ${
                  shape === key
                    ? 'border-brand-600 bg-[color-mix(in_srgb,var(--color-brand-600)_8%,white)] text-ink'
                    : 'border-line-3 bg-surface text-muted'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="sr-only">
            Module shape applies to data modules only; the three finder squares always stay
            standard squares.
          </span>
        </div>
        <div className="flex items-center gap-3.5">
          <span className="flex-none font-jetbrains text-[10px] tracking-[0.08em] text-dim">
            CORNER RADIUS
          </span>
          <span aria-hidden="true" className="relative h-0.5 flex-1 rounded-[2px] bg-line-2">
            <span className="absolute left-0 top-0 h-full w-[30%] rounded-[2px] bg-brand-600" />
            <span className="absolute left-[30%] top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand-600 bg-white" />
          </span>
          <span className="font-jetbrains text-[11.5px]">12 px</span>
        </div>
        <span className="mt-auto pt-1.5 font-jetbrains text-[10px] text-dim">
          params: utm_source=qr (editable, per code) &middot; export: PNG &middot; up to 1000 px
        </span>
      </div>

      <div className="flex min-w-[260px] flex-[0_1_300px] flex-col items-center justify-center gap-3.5 rounded-ref border border-line-2 bg-surface px-[26px] py-6">
        <span className="self-start font-jetbrains text-[10px] tracking-[0.14em] text-dim">
          PREVIEW
        </span>
        <div aria-hidden="true" className="relative w-[190px]">
          <QrSvg
            path={PREVIEW_PATH[shape]}
            className="block w-full rounded-xl border border-line bg-white"
          />
          <span className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[9px] border border-line-2 bg-white font-onest text-lg font-extrabold">
            A
          </span>
        </div>
        <span className="font-jetbrains text-[10.5px] text-dim">go.acme.com/menu &middot; EC M</span>
      </div>
    </div>
  );
}
